import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Copy, Eye, EyeOff, ExternalLink, X } from 'lucide-react';
import api from '../../lib/api';
import ProgramForm from '../../components/ProgramForm';
import ProgramFilters, { applyProgramFilters } from '../../components/ProgramFilters';
import ProgramTable from '../../components/ProgramTable';
import { useToast } from '../../hooks/use-toast';

const MyPrograms = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | program | null
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [price, setPrice] = useState('');
  const [time, setTime] = useState('');

  const fetchMine = async () => {
    setLoading(true);
    try { const res = await api.get('/programs/mine'); setItems(res.data || []); }
    catch (e) { console.error('Failed to load programs:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchMine(); }, []);

  const filtered = useMemo(() => applyProgramFilters(items, { q, type, status, price, time }), [items, q, type, status, price, time]);

  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast({ title: okMsg }); fetchMine(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const publish = (p) => act(() => api.post(`/programs/${p.id}/publish`, null, { params: { value: !p.is_published } }), p.is_published ? 'Unpublished' : 'Published');
  const duplicate = (p) => act(() => api.post(`/programs/${p.id}/duplicate`), 'Duplicated');
  const remove = (p) => { if (window.confirm('Delete this program?')) act(() => api.delete(`/programs/${p.id}`), 'Deleted'); };
  const preview = (p) => window.open(`/programs/${p.slug}`, '_blank');

  if (editing) {
    return (
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif-display text-3xl text-burgundy">{editing === 'new' ? 'New Program' : 'Edit Program'}</h1>
          <button onClick={() => setEditing(null)} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
        </div>
        <ProgramForm program={editing === 'new' ? null : editing} onDone={() => { setEditing(null); fetchMine(); }} onCancel={() => setEditing(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div><div className="eyebrow mb-2">Content</div><h1 className="font-serif-display text-4xl text-burgundy">My <span className="font-italic-display text-rose-italic">Programs</span></h1>
          <p className="mt-2 text-mahogany/80">Create events, retreats &amp; classes. Submissions are reviewed by an admin before going live.</p></div>
        <button onClick={() => setEditing('new')} className="pill-btn"><Plus className="w-4 h-4" /> New Program</button>
      </div>

      <ProgramFilters q={q} setQ={setQ} type={type} setType={setType} status={status} setStatus={setStatus} price={price} setPrice={setPrice} time={time} setTime={setTime} />

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : items.length === 0 ? <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40">No programs yet. Create your first one!</div>
        : (
          <ProgramTable rows={filtered} renderActions={(p) => (
            <>
              <IconBtn onClick={() => setEditing(p)} title="Edit"><Pencil className="w-4 h-4" /></IconBtn>
              <IconBtn onClick={() => publish(p)} title={p.is_published ? 'Unpublish' : 'Publish'}>{p.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</IconBtn>
              <IconBtn onClick={() => duplicate(p)} title="Duplicate"><Copy className="w-4 h-4" /></IconBtn>
              {p.is_published && p.status === 'approved' && <IconBtn onClick={() => preview(p)} title="Preview"><ExternalLink className="w-4 h-4" /></IconBtn>}
              <IconBtn onClick={() => remove(p)} title="Delete" danger><Trash2 className="w-4 h-4" /></IconBtn>
            </>
          )} />
        )}
    </div>
  );
};

const IconBtn = ({ children, danger, ...p }) => (
  <button {...p} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${danger ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-rose-soft/60 text-mahogany hover:bg-rose-soft'}`}>{children}</button>
);

export default MyPrograms;
